/**
 * Renderers for entries in the per-certificate audit log
 * (`certificate_events`). Each renderer turns the typed event_type + payload
 * into a human-readable German summary suitable for the timeline UI.
 *
 * Consuming stories register their event_type → renderer mapping here when
 * they add a new case to `App\Enums\CertificateEventType`.
 */

const RENDERERS = {
    dcv_method_overridden (payload = {}) {
        const from = String(payload.from ?? '').toUpperCase()
        const to = String(payload.to ?? '').toUpperCase()
        const emailPart = payload.email_address ? ` (${payload.email_address})` : ''
        const reasonPart = payload.reason ? ` — Grund: ${payload.reason}` : ''

        return `DCV-Methode von ${from || '—'} auf ${to || '—'} geändert${emailPart}${reasonPart}`
    },
    manual_recheck_requested (payload = {}) {
        const source = payload.source === 'api' ? 'API' : 'web UI'

        return `Manual provider re-check requested via ${source}`
    },
    external_flag_changed (payload = {}) {
        // The timeline already renders the certificate_domain name next to
        // the label, so the renderer only describes the transition.
        return payload.to === true
            ? 'Marked as external (skipped from publish + install)'
            : 'Marked as internal (publish + install resume)'
    },
    primary_domain_changed (payload = {}) {
        const from = payload.from || '—'
        const to = payload.to || '—'

        return `Primary domain changed from ${from} to ${to}`
    },
    san_domains_added (payload = {}) {
        const domains = (payload.domains ?? []).join(', ') || '—'
        const suffix = payload.actor_label ? ` — via ${payload.actor_label}` : ''

        return `SAN(s) added: ${domains}${suffix}`
    },
    san_domains_removed (payload = {}) {
        const domains = (payload.domains ?? []).join(', ') || '—'
        const suffix = payload.actor_label ? ` — via ${payload.actor_label}` : ''

        return `SAN(s) removed: ${domains}${suffix}`
    },
    san_domains_replaced (payload = {}) {
        const added = (payload.added ?? []).join(', ') || '—'
        const removed = (payload.removed ?? []).join(', ') || '—'
        const suffix = payload.actor_label ? ` — via ${payload.actor_label}` : ''

        return `SANs replaced — added: ${added}; removed: ${removed}${suffix}`
    },
}

/**
 * Build a human-readable label for a single audit-log entry.
 *
 * Unknown event_type values fall back to a key=value dump of the payload
 * so the row still carries information even when its renderer hasn't
 * landed yet.
 *
 * @param {string} eventType the `event_type` value (`CertificateEventType::value`)
 * @param {Object} payload   the decoded JSON `payload` column
 * @returns {string}
 */
export function auditEventLabel (eventType, payload) {
    const renderer = RENDERERS[eventType]

    if (renderer) {
        return renderer(payload || {})
    }

    if (!payload || Object.keys(payload).length === 0) {
        return eventType
    }

    return Object.entries(payload)
        .map(([key, value]) => `${key}=${JSON.stringify(value)}`)
        .join(', ')
}
